import React, {
  FC,
  useCallback,
  useMemo,
  memo,
  useRef,
  useEffect,
  useReducer
} from "react";
import styles from "./Keyboard.module.scss";
import { Key } from "./key/Key";
import { Icon, IconButton } from "@material-ui/core";
import {
  ResolutionType,
  useScreenStore
} from "application/contexts/screen/ScreenStore";
import { ScreenStore } from "application/contexts/screen/ScreenStore";
import {
  emailLayout,
  numericClearLayout,
  numericCheckLayout,
  upercaseLayout,
  completeLayout,
  lowercaseLayout,
  getStrongPasswordLayout
} from "./keyboardLayout";

export type KeyboardType =
  | "complete"
  | "numeric"
  | "text"
  | "clear"
  | "email"
  | "strongPassword";

const getLayoutForType = (type?: KeyboardType): string[][] => {
  return type === "complete"
    ? completeLayout
    : type === "strongPassword"
    ? getStrongPasswordLayout()
    : type === "numeric"
    ? numericCheckLayout
    : type === "clear"
    ? numericClearLayout
    : type === "email"
    ? emailLayout
    : type === "text"
    ? lowercaseLayout
    : upercaseLayout;
};

interface KeyboardProps {
  onKeyPress?: (keyPressed: string) => void;
  onPressEnter?: () => void;
  onPressBack?: () => void;
  onPressClear?: () => void;
  type?: KeyboardType;
  label?: string;
  onChange?: (inputValue: string) => void;
  value?: string;
  onClose?: () => void;
  hideClear?: boolean;
  disable?: boolean;
  maxLength?: number;
  hideValue?: boolean;
}

export const Keyboard: FC<KeyboardProps> = memo(
  ({
    onKeyPress,
    onPressEnter,
    onPressBack,
    onPressClear,
    type,
    onChange,
    value,
    label,
    onClose,
    hideClear,
    disable,
    maxLength,
    hideValue,
  }) => {
    const resolution = useScreenStore((s: ScreenStore) => s.resolution);

    const shiftedRef = useRef(false);
    const keyboardLayoutRef = useRef<string[][]>(getLayoutForType(type));
    const valueRef = useRef<string>(value ?? "");

    const onKeyPressRef = useRef(onKeyPress);
    const onPressEnterRef = useRef(onPressEnter);
    const onPressBackRef = useRef(onPressBack);
    const onPressClearRef = useRef(onPressClear);
    const onChangeRef = useRef(onChange);
    const typeRef = useRef(type);
    const maxLengthRef = useRef(maxLength);

    const [layoutVersion, bumpLayoutVersion] = useReducer(
      (x: number) => x + 1,
      0
    );

    const setLayout = useCallback(
      (nextLayout: string[][]) => {
        keyboardLayoutRef.current = nextLayout;
        bumpLayoutVersion();
      },
      [bumpLayoutVersion]
    );

    useEffect(() => {
      onKeyPressRef.current = onKeyPress;
    }, [onKeyPress]);

    useEffect(() => {
      onPressEnterRef.current = onPressEnter;
    }, [onPressEnter]);

    useEffect(() => {
      onPressBackRef.current = onPressBack;
    }, [onPressBack]);

    useEffect(() => {
      onPressClearRef.current = onPressClear;
    }, [onPressClear]);

    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
      maxLengthRef.current = maxLength;
    }, [maxLength]);

    useEffect(() => {
      typeRef.current = type;
    }, [type]);

    useEffect(() => {
      valueRef.current = value ?? "";
    }, [value]);

    useEffect(() => {
      shiftedRef.current = false;
      setLayout(getLayoutForType(type));
    }, [type, setLayout]);

    const onPressKey = useCallback((keyPressed: string) => {
      const currentType = typeRef.current;
      const currentValue = valueRef.current ?? "";
      const currentMaxLength = maxLengthRef.current;

      if (keyPressed === "check") {
        onPressEnterRef.current?.();
        return;
      }

      if (keyPressed === "back-space") {
        onPressBackRef.current?.();
        const next = currentValue ? currentValue.substring(0, currentValue.length - 1) : "";
        valueRef.current = next;
        onChangeRef.current?.(next);
        return;
      }

      if (keyPressed === "shift") {
        const nextShifted = !shiftedRef.current;
        shiftedRef.current = nextShifted;
        setLayout(nextShifted ? upercaseLayout : lowercaseLayout);
        return;
      }

      if (keyPressed === "clear") {
        onPressClearRef.current?.();
        valueRef.current = "";
        onChangeRef.current?.("");
        return;
      }

      if (currentMaxLength && currentValue.length >= currentMaxLength) {
        return;
      }

      onKeyPressRef.current?.(keyPressed);
      const next = currentValue + keyPressed;
      valueRef.current = next;
      onChangeRef.current?.(next);

      if ((!currentType || currentType === "text") && shiftedRef.current) {
        shiftedRef.current = false;
        setLayout(lowercaseLayout);
      }
    }, [setLayout]);

    const keyboardRender = useMemo(
      () => {
        const keyboardLayout = keyboardLayoutRef.current;
        return keyboardLayout.map((line, k) => (
          <div className={styles.keyboardLine} key={k} data-layout-version={layoutVersion}>
            {line.map((key, _key) => (
              <div
                key={_key}
                style={{
                  padding: type === "numeric" || type === "clear" ? 8 : 4,
                  flex:
                    (key === "check" || key === "back-space") &&
                    type !== "numeric" &&
                    type !== "clear" &&
                    type !== "strongPassword"
                      ? 2
                      : 1,
                }}
                className={styles.containerKeyboardKey}
              >
                {hideClear && key === "clear" ? null : (
                  <Key
                    type={
                      type === "numeric" || type === "clear"
                        ? "number"
                        : type === "strongPassword"
                        ? "password"
                        : "letter"
                    }
                    disable={disable}
                    onClick={onPressKey}
                  >
                    {key}
                  </Key>
                )}
              </div>
            ))}
          </div>
        ));
      },
      [layoutVersion, type, hideClear, disable, onPressKey]
    );

    const layoutLength = keyboardLayoutRef.current.length;

    return (
      <div id={styles.Keyboard}>
        {onClose && (
          <div className={styles.keyboardOnClose}>
            <IconButton onClick={onClose}>
              <Icon fontSize={"large"}>close</Icon>
            </IconButton>
          </div>
        )}
        {label && (
          <div
            className={styles.keyboardLabel}
            style={{
              minHeight: resolution === ResolutionType.FULL_HD ? 90 : 70,
            }}
          >
            {label}
          </div>
        )}
        {!hideValue && (
          <div
            className={styles.keyboardValue}
            style={{
              minHeight: resolution === ResolutionType.FULL_HD ? 80 : 60,
            }}
          >
            {value && !maxLength ? value : ""}
          </div>
        )}
        <div
          style={{
            maxHeight:
              type === "strongPassword"
                ? undefined
                : layoutLength *
                    (type === "numeric" || type === "clear"
                      ? resolution === ResolutionType.FULL_HD
                        ? 160
                        : 80
                      : 85) +
                  (hideValue ? 0 : 80),
          }}
        >
          {keyboardRender}
        </div>
      </div>
    );
  }
);
